/* ============================================================================
   FitLibrary — fitlibrary-review-source.js
   THE SINGLE REVIEW SOURCE. Phase 1 of the review-system migration.

   Every review surface in the theme talks to this module and nothing else.
   It owns the normalized review model, the product catalogue, the rating
   maths and the two queries (product highlights, landing highlights). It owns
   no markup and no styling.

   WHY THIS EXISTS
   Before this file the store had two unrelated review systems and eleven
   copies of the data: the Landing carousel read eight reviews out of
   templates/index.json, and each of the ten product pages carried its own six
   reviews as schema settings. Nothing could be counted, nothing could be
   moderated, and a rating shown to a customer was whatever someone had typed
   into a theme setting. This module replaces that with one dataset, one set of
   maths and one contract.

   THE PROVIDER CONTRACT — this is the seam Phase 2 replaces.
   A provider is any object with:

     name          {string}   identifier, surfaced by FitLibraryReviews.providerName
     isPersistent  {boolean}  true only for a real server-side store
     getProducts() {object}   { map: { <handle>: ProductRef }, order: [handle] }
     getReviews()  {array}    raw records, any status, normalized by this module
     submitReview(payload) {Promise} resolves { ok, status } / rejects Error

   Phase 1 ships ONE provider: `themeJsonProvider`, which reads the two
   <script type="application/json"> islands the Landing section renders. It is
   READ-ONLY by construction — its submitReview always rejects. Phase 2 drops
   in an App Proxy or review-app provider and no page component changes.

   WHAT THIS FILE DELIBERATELY DOES NOT DO
   · It does not write. No localStorage, no sessionStorage, no cookie, no
     theme setting, no in-memory array pretending to be a database.
   · It holds no credential, token or Admin API endpoint, and never will —
     a storefront script is public.
   · It never treats a submission as a publication.
   · It never fabricates `verifiedPurchase`.

   RATING MATHS — the rule that must not be broken.
   An average is computed from EVERY approved review for the exact product.
   The 4.0 highlight threshold decides what a carousel is allowed to SHOW; it
   never touches what an average is allowed to COUNT. A 2-star approved review
   is invisible in the carousel and fully present in the average. These are two
   different queries over one dataset and they are kept apart on purpose.
   ============================================================================ */

(function (window, document) {
  "use strict";

  /* ============================= COLLECTION MODE =============================

     THE BUSINESS STATE THIS STORE IS IN, WRITTEN DOWN ONCE.

     FitLibrary is COLLECTING real customer reviews and DISPLAYING none of
     them. Reading and writing are two different questions now, and this is the
     one place that answers both:

       display: "sample_only"     Every review card, average and count on the
                                  storefront comes from the sixty-seven sample
                                  reviews. A real review NEVER reaches a card,
                                  a carousel, an average or a count, however
                                  many are collected.

       submissions: "enabled"     The Write a Review form posts to the real
                                  backend through the Shopify App Proxy.

       publication: "collect_only"  Every real submission is stored as an
                                  unpublished DRAFT and left there, as a review
                                  bank for a later migration. Nothing on the
                                  storefront or in the Worker publishes one.

     THIS IS NOT A COMMENT, IT IS A GATE. While display is "sample_only",
     readProvider() below returns the sample provider no matter what anyone
     registered — so registering a persistent provider cannot quietly start
     feeding real reviews into the cards. Turning real reviews on is a
     deliberate edit here plus the display work that goes with it, not an
     accident somebody has on a Tuesday.

     THE FUTURE MODE IS NOT IMPLEMENTED. "real" is named below so the intent is
     legible; there is no branch that serves it, and adding the name did not
     create one.

     The Worker states its own half in src/worker.js as
     REAL_REVIEW_PUBLICATION_MODE. They are separate runtimes and each says its
     half out loud, so neither can be read as evidence about the other. */

  var DISPLAY_MODES = { SAMPLE_ONLY: "sample_only", REAL: "real" };

  var MODE = {
    display: DISPLAY_MODES.SAMPLE_ONLY,
    submissions: "enabled",
    publication: "collect_only"
  };

  /* FROZEN ON PURPOSE. Changing what the storefront displays is an edit to
     this file, reviewed and deployed — not something a console, an app embed
     or a third-party script can do at runtime by assigning to MODE.display.
     Under strict mode an attempt throws rather than silently succeeding, so a
     mistaken flip is loud and immediate instead of quietly publishing real
     reviews to customers. */
  if (Object.freeze) { Object.freeze(MODE); Object.freeze(DISPLAY_MODES); }

  /* The one configurable visibility threshold. Highlight modules only. */
  var MIN_HIGHLIGHT_RATING = 4.0;

  var STATUS = { PENDING: "pending", APPROVED: "approved", REJECTED: "rejected" };

  /* ---------------------------------------------------------------- utils */

  function esc(s) {
    return String(s == null ? "" : s)
      .split("&").join("&amp;")
      .split("<").join("&lt;")
      .split(">").join("&gt;")
      .split('"').join("&quot;")
      .split("'").join("&#39;");
  }

  function fmtDate(iso) {
    if (!iso) return "";
    var d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    try {
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  }

  function clampRating(n) {
    var v = Number(n);
    if (!isFinite(v)) return 0;
    if (v < 0) return 0;
    if (v > 5) return 5;
    return v;
  }

  /* Reviewer age. A whole number in the adult range or nothing at all — there
     is no "unknown" placeholder and no zero shown to a customer. The floor is
     18, the same floor the Worker's LIMITS.age and the `age` field on the
     fitlibrary_review metaobject enforce; anything outside the bounds is
     treated as absent and the name renders on its own, exactly as it did
     before ages existed. */
  function clampAge(n) {
    if (n == null || n === "") return 0;
    var v = Number(n);
    if (!isFinite(v)) return 0;
    v = Math.floor(v);
    if (v < 18 || v > 120) return 0;
    return v;
  }

  /* THE ONE PLACE THE REVIEWER LINE IS COMPOSED.

     Every surface — the Landing carousel and all ten product carousels —
     calls this, so "NAME, AGE" is written once and cannot drift into eleven
     slightly different formats. The name is never rewritten to carry the age:
     `reviewerDisplayName` stays exactly what the record says, `age` is a
     separate field, and the comma-space join lives here alone.

     No age, or an age outside the sane range, yields the bare name. */
  function reviewerLine(review) {
    review = review || {};
    var name = String(review.reviewerDisplayName || "");
    if (!name) return "";
    var age = clampAge(review.age);
    return age ? name + ", " + age : name;
  }

  /* ------------------------------------------------------- normalized model
     Every record entering the system is coerced to this shape, whatever the
     provider handed over. Unknown fields are dropped rather than passed
     through, so a future provider cannot leak an email or an internal id into
     the public model by accident. */

  function normalizeReview(raw, index) {
    if (!raw || typeof raw !== "object") return null;

    var handle = raw.productHandle || raw.product || "";
    /* FAIL CLOSED. A record with no status, an unrecognised status, or a
       status this build does not know about is PENDING — never APPROVED.
       Publication has to be asserted by whoever owns the data; it is never a
       side effect of a missing field. Every intentionally public seed record
       carries "status": "approved" explicitly. */
    var status = String(raw.status || STATUS.PENDING).toLowerCase();
    if (status !== STATUS.PENDING && status !== STATUS.APPROVED && status !== STATUS.REJECTED) {
      status = STATUS.PENDING;
    }

    var created = raw.createdAt || raw.date || "";

    return {
      reviewId: String(raw.reviewId || raw.id || ("rv-" + index)),
      productId: raw.productId ? String(raw.productId) : "",
      productHandle: String(handle),
      rating: clampRating(raw.rating),
      title: String(raw.title || ""),
      body: String(raw.body || ""),
      reviewerDisplayName: String(raw.reviewerDisplayName || raw.name || raw.author || ""),
      /* Carried through as its own field and never folded into the display
         name. Surfaces render it via reviewerLine() below; a record without
         one shows the name alone. */
      age: clampAge(raw.age),
      status: status,
      createdAt: created,
      updatedAt: raw.updatedAt || created,
      /* Never inferred. Only ever true when a provider asserts it from a real
         purchase-verification mechanism. */
      verifiedPurchase: raw.verifiedPurchase === true || raw.verified === true,
      source: String(raw.source || "theme-demo"),
      isDemo: raw.isDemo === true || raw.is_demo === true,
      /* UI affordance carried through unchanged; not part of the rating maths. */
      helpful: Number(raw.helpful) || 0
    };
  }

  function normalizeProduct(key, raw) {
    raw = raw || {};
    return {
      productId: raw.productId ? String(raw.productId) : "",
      handle: String(raw.handle || key || ""),
      name: String(raw.name || raw.title || ""),
      family: String(raw.family || ""),
      type: String(raw.type || ""),
      url: String(raw.url || "#"),
      accent: String(raw.accent || "var(--accent)")
    };
  }

  /* --------------------------------------------------- theme JSON provider

     Phase 1's only provider. Reads whatever the Landing section rendered into
     the page as two JSON islands. Read-only: a theme is not a database, so
     submitReview rejects rather than pretending. */

  function themeJsonProvider(scope) {
    var root = scope || document;

    function readIsland(selector) {
      var el = root.querySelector(selector);
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    return {
      name: "theme-json",
      isPersistent: false,
      getProducts: function () {
        var p = readIsland("[data-rv-products]") || {};
        return { map: p.map || {}, order: p.order || [] };
      },
      getReviews: function () {
        return readIsland("[data-rv-reviews]") || [];
      },
      submitReview: function () {
        return Promise.reject(new Error(
          "No persistent review backend is connected. Phase 2 must supply a " +
          "provider with isPersistent: true before submissions can be stored."
        ));
      }
    };
  }

  /* ------------------------------------------------------------- the source */

  /* TWO SEAMS, NOT ONE.

     `provider` is where reviews are READ from. `writeProvider` is where a
     submission is SENT. They used to be the same object, which tied "can a
     customer submit a review?" to "is the display fed by a real backend?" —
     and those are now deliberately different answers: the form writes to
     production while the cards keep showing samples. Collection Mode is
     exactly that split, so the split is in the code. */
  var provider = null;
  var writeProvider = null;
  var sampleProvider = null;
  var cacheProducts = null;
  var cacheReviews = null;

  function sample() {
    if (!sampleProvider) sampleProvider = themeJsonProvider(document);
    return sampleProvider;
  }

  /* THE READ GATE. Every public read in this file goes through here.

     While MODE.display is "sample_only" this returns the sample provider even
     if a persistent one has been registered. That is the point: a future
     developer who wires up a real provider gets no display change and no
     silent cutover — the cards keep showing samples until someone changes
     MODE.display on purpose and does the display work that goes with it. */
  function readProvider() {
    if (MODE.display === DISPLAY_MODES.SAMPLE_ONLY) return sample();
    return provider || sample();
  }

  function ensure() {
    var src = readProvider();
    if (!provider) provider = src;
    if (!cacheProducts) {
      var p = src.getProducts() || { map: {}, order: [] };
      var map = {};
      var order = [];
      (p.order || []).forEach(function (key) {
        if (p.map && p.map[key]) { map[key] = normalizeProduct(key, p.map[key]); order.push(key); }
      });
      /* Any catalogue entry not named in `order` is still usable by lookup. */
      Object.keys(p.map || {}).forEach(function (key) {
        if (!map[key]) map[key] = normalizeProduct(key, p.map[key]);
      });
      cacheProducts = { map: map, order: order };
    }
    if (!cacheReviews) {
      cacheReviews = (src.getReviews() || [])
        .map(normalizeReview)
        .filter(Boolean)
        /* A review with no product association cannot belong to the system.
           Association is by handle, resolved to a real catalogue entry — never
           by family, title text or template name. */
        .filter(function (r) { return r.productHandle && cacheProducts.map[r.productHandle]; })
        .map(function (r) {
          /* Backfill the canonical Shopify product id from the catalogue when
             the record carried only a handle. */
          if (!r.productId) r.productId = cacheProducts.map[r.productHandle].productId || "";
          return r;
        });
    }
  }

  function invalidate() { cacheProducts = null; cacheReviews = null; }

  /* The one refresh path. Drops the caches so the next read re-normalizes from
     the provider, then tells every mounted surface to re-query. Surfaces listen
     for `fitlibrary:reviews-updated` and re-run their own two questions — no
     surface re-implements a filter or an average to stay in sync. */
  var UPDATED_EVENT = "fitlibrary:reviews-updated";

  function refresh() {
    invalidate();
    try {
      document.dispatchEvent(new CustomEvent(UPDATED_EVENT));
    } catch (e) {
      /* Older engines: CustomEvent constructor unavailable. */
      try {
        var ev = document.createEvent("CustomEvent");
        ev.initCustomEvent(UPDATED_EVENT, true, false, null);
        document.dispatchEvent(ev);
      } catch (e2) {}
    }
  }

  /* REGISTER A READ PROVIDER. Under Collection Mode this stores the provider
     and changes nothing on screen, because readProvider() is still returning
     the sample set — see MODE at the top of this file. It is not a no-op
     forever; it is the seam the future display cutover will use. */
  function setProvider(p) {
    if (!p || typeof p.getReviews !== "function" || typeof p.getProducts !== "function") {
      throw new Error("FitLibraryReviews.setProvider: invalid provider.");
    }
    provider = p;
    /* Swapping the provider is the Phase 2B switch. Refreshing here is what
       makes that a one-line change: every surface redraws from the new data
       without being touched. */
    refresh();
  }

  /* REGISTER THE SUBMISSION BACKEND. Separate from setProvider on purpose:
     this is what makes the Write a Review form live while the cards stay on
     the sample set. It touches no cache and fires no refresh, because a
     submission changes nothing that is displayed. */
  function setWriteProvider(p) {
    if (!p || typeof p.submitReview !== "function") {
      throw new Error("FitLibraryReviews.setWriteProvider: invalid write provider.");
    }
    writeProvider = p;
  }

  /* The question the Write a Review form actually needs answered: is there a
     real backend to send this to? It used to ask isPersistent(), which is a
     question about the DISPLAY source and is deliberately false right now. */
  function submissionsEnabled() {
    return !!(writeProvider && writeProvider.isPersistent === true &&
              typeof writeProvider.submitReview === "function");
  }

  /* Resolve by handle first, then by numeric/gid product id. */
  function product(idOrHandle) {
    ensure();
    if (!idOrHandle) return null;
    var key = String(idOrHandle);
    if (cacheProducts.map[key]) return cacheProducts.map[key];
    var hit = null;
    Object.keys(cacheProducts.map).forEach(function (h) {
      var p = cacheProducts.map[h];
      /* Accepts a bare numeric id or a full gid://shopify/Product/123 — the
         trailing segment is taken with split rather than a regex so the source
         stays free of backslash escapes. */
      if (!hit && p.productId && (p.productId === key || p.productId === key.split("/").pop())) hit = p;
    });
    return hit;
  }

  function products() { ensure(); return cacheProducts.map; }
  function productOrder() { ensure(); return cacheProducts.order.slice(); }

  /* Every public read starts here. Only APPROVED reviews exist publicly:
     pending and rejected records never reach a carousel, a count, an average
     or a distribution. */
  function all() {
    ensure();
    return cacheReviews.filter(function (r) { return r.status === STATUS.APPROVED; });
  }

  /* Moderation / diagnostics only — never render this. */
  function allRecords() { ensure(); return cacheReviews.slice(); }

  function byNewest(a, b) {
    var ta = Date.parse(a.createdAt) || 0;
    var tb = Date.parse(b.createdAt) || 0;
    return tb - ta;
  }

  /* ------------------------------------------------------------ rating maths

     summary() is the ONLY place an average is produced. Both the product
     summary and the global summary call it with a complete approved set, never
     with a highlighted subset. */

  function summary(list) {
    var arr = (list || []).filter(function (r) { return r && r.status === STATUS.APPROVED; });
    var distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    var sum = 0;
    var counted = 0;

    arr.forEach(function (r) {
      var v = clampRating(r.rating);
      if (v <= 0) return;
      sum += v;
      counted++;
      var bucket = Math.round(v);
      if (bucket < 1) bucket = 1;
      if (bucket > 5) bucket = 5;
      distribution[bucket]++;
    });

    var average = counted ? sum / counted : 0;

    return {
      count: counted,
      /* Unrounded, so a caller can decide its own precision. */
      average: average,
      /* One decimal, the figure shown to a customer: 4.25 -> "4.3". */
      averageDisplay: counted ? (Math.round(average * 10) / 10).toFixed(1) : "",
      distribution: distribution,
      /* Percentages for a distribution bar, safe when count is 0. */
      distributionPercent: [5, 4, 3, 2, 1].reduce(function (acc, star) {
        acc[star] = counted ? (distribution[star] / counted) * 100 : 0;
        return acc;
      }, {})
    };
  }

  /* PRODUCT AVERAGE: every approved review for that exact product. */
  function productSummary(idOrHandle) {
    var p = product(idOrHandle);
    if (!p) return summary([]);
    return summary(all().filter(function (r) { return r.productHandle === p.handle; }));
  }

  /* GLOBAL AVERAGE: every approved review across all products. Not the 4.0+
     subset — that difference is intentional and is the whole point of the
     landing rating rule. */
  function globalSummary() { return summary(all()); }

  /* ------------------------------------------------------------- the queries

     Two, and only two. Both read the same dataset. */

  /* PRODUCT HIGHLIGHTS — approved AND this exact product AND rating >= 4.0. */
  function productReviews(idOrHandle, options) {
    options = options || {};
    var min = options.minRating == null ? MIN_HIGHLIGHT_RATING : options.minRating;
    var p = product(idOrHandle);
    if (!p) return [];
    var list = all()
      .filter(function (r) { return r.productHandle === p.handle && r.rating >= min; })
      .sort(byNewest);
    return options.limit ? list.slice(0, options.limit) : list;
  }

  /* LANDING HIGHLIGHTS — approved AND rating >= 4.0 across all ten products,
     then diversified so one product cannot fill a whole view.

     Diversification is a round-robin over per-product queues, each already in
     newest-first order. It reorders; it never filters. If only one product has
     eligible reviews the result is simply that product's reviews. */
  function landing(options) {
    options = options || {};
    var min = options.minRating == null ? MIN_HIGHLIGHT_RATING : options.minRating;
    var diversify = options.diversify !== false;

    var eligible = all()
      .filter(function (r) { return r.rating >= min; })
      .sort(byNewest);

    if (!diversify) return options.limit ? eligible.slice(0, options.limit) : eligible;

    var queues = {};
    var order = [];
    eligible.forEach(function (r) {
      if (!queues[r.productHandle]) { queues[r.productHandle] = []; order.push(r.productHandle); }
      queues[r.productHandle].push(r);
    });

    var out = [];
    var drained = false;
    while (!drained) {
      drained = true;
      for (var i = 0; i < order.length; i++) {
        var q = queues[order[i]];
        if (q && q.length) { out.push(q.shift()); drained = false; }
      }
    }

    return options.limit ? out.slice(0, options.limit) : out;
  }

  /* ------------------------------------------------------------- submission

     Phase 2's entry point. Phase 1 rejects, loudly and honestly, so no surface
     can ever tell a customer their review was saved when it was not. The
     payload shape below IS the contract a Phase 2 provider must accept.

       { productId, productHandle, rating, title, body,
         reviewerDisplayName, email, age, consent }

     `email` is passed to the provider and must never be echoed back into any
     public read model. `status` is assigned by the backend, always `pending`
     for a new submission — never by this module and never by a form. */
  function submit(payload) {
    ensure();
    var p = payload && (payload.productHandle || payload.product);
    var resolved = p ? product(p) : null;
    if (!resolved) return Promise.reject(new Error("Unknown product."));
    var record = {
      productId: resolved.productId,
      productHandle: resolved.handle,
      rating: clampRating(payload.rating),
      title: String(payload.title || ""),
      body: String(payload.body || ""),
      reviewerDisplayName: String(payload.reviewerDisplayName || payload.name || ""),
      email: payload.email == null ? "" : String(payload.email),
      consent: payload.consent === true,
      source: "storefront-form"
    };

    /* Age is optional and is carried ONLY when the reviewer gave one. Blank
       means the key is absent, not an empty string — the backend's model
       distinguishes "did not say" from "said nothing", and sending "" would
       collapse the two. The value is passed through as typed; the Worker is
       what decides whether it is a real age, because it is the only side that
       cannot be edited by whoever is holding the browser. */
    var suppliedAge = payload.age;
    if (suppliedAge !== undefined && suppliedAge !== null &&
        String(suppliedAge).trim() !== "") {
      record.age = String(suppliedAge).trim();
    }

    /* Writes go to the write provider. Falling back to the read provider is
       what keeps the honest Phase 1 rejection working when no submission
       backend has been registered: the sample provider's submitReview rejects
       rather than pretending. */
    var target = writeProvider || readProvider();
    if (!target || typeof target.submitReview !== "function") {
      return Promise.reject(new Error("No review submission backend is connected."));
    }
    return target.submitReview(record);
  }

  /* A statement about the DISPLAY source, and false throughout Collection
     Mode. It is not the question a form should ask — see submissionsEnabled. */
  function isPersistent() { ensure(); return readProvider().isPersistent === true; }

  /* --------------------------------------------------------- star rendering

     One star language for the whole store: yellow fill, thin dark outline,
     white outlined empties. Fractional by construction — 4.3 renders as four
     filled stars and a fifth filled to 30%, not a fixed half. Matches
     snippets/fitlibrary-nr-stars.liquid so server-rendered and JS-rendered
     stars are indistinguishable. */

  function starIcon(fillPercent) {
    var d = "M12 17.3l-6.16 3.7 1.64-7.03L2 9.24l7.19-.62L12 2l2.81 6.62L22 9.24l-5.48 4.73 1.64 7.03z";
    /* Rounded to a whole percent. Without this, 4.3 produces 30.000000000000004
       and the clip reads `inset(0 69.99999999999999% 0 0)` — visually the same,
       but noise in the DOM and impossible to assert against. Also keeps the
       output identical to the Liquid snippet, which computes in integers. */
    var pct = Math.round(Math.max(0, Math.min(100, fillPercent == null ? 100 : fillPercent)));
    var style = pct >= 100 ? "" : (pct <= 0
      ? ' style="display:none"'
      : ' style="clip-path:inset(0 ' + (100 - pct) + '% 0 0)"');
    return '<span class="nr-star">' +
      '<svg class="nr-star__base" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="' + d + '" fill="#FFFFFF" stroke="#1D2535" stroke-width="1.1" stroke-linejoin="round"/></svg>' +
      '<svg class="nr-star__fill" viewBox="0 0 24 24" aria-hidden="true" focusable="false"' + style + '><path d="' + d + '" fill="#E0A82E" stroke="#1D2535" stroke-width="1.1" stroke-linejoin="round"/></svg>' +
      '</span>';
  }

  function starsHTML(rating, label) {
    var v = clampRating(rating);
    var out = "";
    for (var i = 1; i <= 5; i++) {
      var pct = Math.max(0, Math.min(1, v - (i - 1))) * 100;
      out += starIcon(pct);
    }
    /* Formatted exactly as snippets/fitlibrary-nr-stars.liquid formats it —
       one decimal place, and no trailing ".0" on a whole value — so a
       JS-rendered card and a Liquid-rendered one announce the same thing to a
       screen reader. Liquid computes `rating | times: 10 | round |
       divided_by: 10.0`, which prints 5 as "5" and 4.5 as "4.5". */
    var aria = label || (v ? String(Math.round(v * 10) / 10) + " out of 5 stars" : "Not yet rated");
    return '<span class="nr-stars" role="img" aria-label="' + esc(aria) + '">' + out + "</span>";
  }

  /* ------------------------------------------------------------------ export */

  window.FitLibraryReviews = {
    MIN_HIGHLIGHT_RATING: MIN_HIGHLIGHT_RATING,
    STATUS: STATUS,

    /* Collection Mode. Read it before assuming anything about where a review
       on this page came from, or where a submitted one went. */
    MODE: MODE,
    DISPLAY_MODES: DISPLAY_MODES,

    /* read seam */
    setProvider: setProvider,
    themeJsonProvider: themeJsonProvider,
    refresh: refresh,
    UPDATED_EVENT: UPDATED_EVENT,
    get providerName() { ensure(); return readProvider().name; },
    isPersistent: isPersistent,

    /* write seam — independent of the read seam by design */
    setWriteProvider: setWriteProvider,
    submissionsEnabled: submissionsEnabled,
    get writeProviderName() { return writeProvider ? writeProvider.name : ""; },

    /* catalogue */
    product: product,
    products: products,
    get productOrder() { return productOrder(); },

    /* reads */
    all: all,
    allRecords: allRecords,
    productReviews: productReviews,
    landing: landing,

    /* maths */
    summary: summary,
    productSummary: productSummary,
    globalSummary: globalSummary,

    /* writes (Phase 2) */
    submit: submit,

    /* shared helpers so every surface renders identically */
    reviewerLine: reviewerLine,
    esc: esc,
    fmtDate: fmtDate,
    starIcon: starIcon,
    starsHTML: starsHTML
  };
})(window, document);